import React from "react";
import { icons, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

const OVERRIDES = {
  grid: "Grid3x3",
  "edit-3": "PenLine",
  edit: "Pencil",
};

const toPascal = (n) =>
  n.split(/[-_]/).map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join("");

export function Icon({ name, size = 20, className, strokeWidth = 1.8, style }) {
  const key = OVERRIDES[name] || toPascal(name || "circle");
  const Cmp = icons[key] || icons.Circle;
  return <Cmp size={size} className={className} strokeWidth={strokeWidth} style={style} />;
}

export function Label({ children, className }) {
  return (
    <span className={cn("mb-1.5 block text-[13px] font-medium text-ink-3", className)}>
      {children}
    </span>
  );
}

export function Field({ label, testId, multiline, className, ...props }) {
  return (
    <label className="mb-4 block">
      {label ? <Label>{label}</Label> : null}
      {multiline ? (
        <textarea data-testid={testId} className={cn("field-input min-h-[110px] resize-y", className)} {...props} />
      ) : (
        <input data-testid={testId} className={cn("field-input", className)} {...props} />
      )}
    </label>
  );
}

export function ChoiceCard({ label, sub, icon, selected, onClick, testId, className }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={cn(
        "flex flex-1 flex-col items-center justify-center gap-2 rounded-2xl border-[1.5px] px-3 py-5 text-center transition-all duration-200 active:scale-[0.98]",
        selected
          ? "border-brand bg-brand-tertiary shadow-sm"
          : "border-line bg-white hover:border-brand-secondary hover:bg-paper-3",
        className
      )}
    >
      {icon ? <Icon name={icon} size={22} className={selected ? "text-brand" : "text-ink-3"} /> : null}
      <span className={cn("text-[15px] font-semibold", selected ? "text-brand-on" : "text-ink")}>{label}</span>
      {sub ? <span className="text-xs text-mute">{sub}</span> : null}
    </button>
  );
}

export function Toggle({ label, value, onChange, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={() => onChange(!value)}
      className="mb-3 flex w-full items-center justify-between rounded-xl border border-line bg-white px-4 py-3 text-left transition-colors hover:border-brand-secondary"
    >
      <span className="pr-3 text-[15px] font-medium text-ink">{label}</span>
      <span
        className={cn(
          "relative h-7 w-12 shrink-0 rounded-full p-[3px] transition-colors duration-200",
          value ? "bg-brand" : "bg-line-2"
        )}
      >
        <span
          className={cn(
            "block h-[22px] w-[22px] rounded-full bg-white shadow transition-transform duration-200",
            value ? "translate-x-5" : "translate-x-0"
          )}
        />
      </span>
    </button>
  );
}

export function PrimaryButton({ title, onClick, loading, disabled, testId, icon, type = "button", className }) {
  return (
    <button
      type={type}
      data-testid={testId}
      disabled={disabled || loading}
      onClick={onClick}
      className={cn(
        "flex min-h-[54px] w-full items-center justify-center gap-2 rounded-full bg-brand px-6 text-[16px] font-semibold text-white transition-all duration-200 hover:brightness-105 active:scale-[0.99] disabled:opacity-50",
        className
      )}
    >
      {loading ? (
        <Loader2 className="animate-spin" size={20} />
      ) : (
        <>
          <span>{title}</span>
          {icon ? <Icon name={icon} size={18} className="text-white" /> : null}
        </>
      )}
    </button>
  );
}

export function GhostButton({ title, onClick, testId, icon, className }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={cn(
        "flex min-h-[52px] flex-1 items-center justify-center gap-2 rounded-xl border-[1.5px] border-brand-tertiary bg-white px-4 text-[14px] font-semibold text-brand-on transition-colors hover:bg-paper-3",
        className
      )}
    >
      {icon ? <Icon name={icon} size={18} className="text-brand" /> : null}
      <span>{title}</span>
    </button>
  );
}
